import { unwrap, type Api } from "../api/index.js";
import { isValidEmail } from "../auth/validation.js";
import { ApiError, NetworkError, toRecoveryAction, type RecoveryAction } from "../recovery/index.js";

/**
 * Identity verification (W-7 SG, W-8 HK), headless. SG: SingPass (Myinfo v5) by full-page
 * redirect and return — no polling.
 *
 *   1. `init()` → the SingPass authorise URL (`platform=customer_web`); the
 *      screen stores `returnTo` in sessionStorage and leaves the page.
 *   2. The SingPass service redirects back to
 *      `/account/verify?singpass_session=<token>` (or `?singpass_error=<code>`).
 *   3. `getSession(token)` reads Myinfo once (completed → consumed). Myinfo
 *      stays in memory only.
 *   4. `authenticate()` (consumed → verified): the server takes the identity
 *      fields from the session and sets `isAuthenticated` itself.
 *
 * React hooks stay in customer_web.
 */

/** Myinfo person data as returned by `GET /api/singpass/session/:token`. */
export interface MyinfoPerson {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  dob: string;
  nric: string;
  gender: string;
  address: string;
  mobile: string;
  occupation: string;
  maritalStatus: string;
  housingType: string;
  hdbType: string;
  ownerPrivate: string;
  nationality: string;
  genderDesc: string;
  nationalityDesc: string;
  maritalStatusDesc: string;
  housingTypeDesc: string;
  hdbTypeDesc: string;
}

/** The SingPass service's customer-web platform (its success and error redirects). */
export const SINGPASS_PLATFORM = "customer_web";

/** sessionStorage key for where to go after verifying (not a secret; re-validated on read). */
export const VERIFY_RETURN_TO_KEY = "kn.verify.returnTo";
/**
 * sessionStorage marker: a SingPass return reached this tab. Never the token.
 * Lets a reload (token already stripped and spent) or a sign-in detour show
 * the "session ended" recovery instead of a fresh start.
 */
export const VERIFY_RETURNED_KEY = "kn.verify.returned";

/** Error codes the SingPass service puts in `?singpass_error=` for customer_web. */
export const SINGPASS_ERROR_CODES = ["cancelled", "expired", "failed"] as const;
export type SingpassErrorCode = (typeof SINGPASS_ERROR_CODES)[number];

/** Every failure the verify screen can show (`errors.singpass_<code>`). */
export type SingpassFailure = SingpassErrorCode | "session_lost" | "rate_limited";
export const SINGPASS_FAILURES: readonly SingpassFailure[] = [
  ...SINGPASS_ERROR_CODES,
  "session_lost",
  "rate_limited",
];

/** An unknown `singpass_error` value is treated as `failed` (never shown raw). */
export function parseSingpassError(raw: string | null | undefined): SingpassErrorCode | undefined {
  if (raw === null || raw === undefined) return undefined;
  return (SINGPASS_ERROR_CODES as readonly string[]).includes(raw) ? (raw as SingpassErrorCode) : "failed";
}

/** A non-2xx (or unreachable) SingPass service call. */
export class SingpassError extends Error {
  readonly failure: SingpassFailure;
  readonly status?: number;
  constructor(failure: SingpassFailure, status?: number) {
    super(`SingPass ${failure}${status ? ` (HTTP ${status})` : ""}`);
    this.name = "SingpassError";
    this.failure = failure;
    this.status = status;
  }
}

export interface SingpassApi {
  /** Starts SingPass; resolves the authorise URL. The init `sessionToken` is ignored on web. */
  init(): Promise<{ redirectUrl: string }>;
  /** Reads Myinfo once; a spent or unknown token → `SingpassError("session_lost")`. */
  getSession(token: string): Promise<MyinfoPerson>;
}

export function createSingpassApi(
  baseUrl: string,
  fetchImpl: typeof fetch = (...a) => fetch(...a),
  timeoutMs = 30_000,
): SingpassApi {
  const base = baseUrl.replace(/\/+$/, "");
  const get = async <T>(path: string, notFound: SingpassFailure): Promise<T> => {
    // Same bound as the region api client: a hung service ends in a RecoveryView, not an endless spinner.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res: Response;
    try {
      res = await fetchImpl(new Request(`${base}${path}`, { method: "GET", signal: controller.signal }));
    } catch (err) {
      throw new NetworkError(err, controller.signal.aborted);
    } finally {
      clearTimeout(timer);
    }
    if (!res.ok) {
      if (res.status === 429) throw new SingpassError("rate_limited", 429);
      if (res.status === 404) throw new SingpassError(notFound, 404);
      throw new SingpassError("failed", res.status);
    }
    return (await res.json()) as T;
  };
  return {
    async init() {
      const q = new URLSearchParams({
        platform: SINGPASS_PLATFORM,
        callbackContext: SINGPASS_PLATFORM,
        purpose: SINGPASS_PLATFORM,
      });
      const r = await get<{ data: { redirectUrl: string } }>(`/api/singpass/auth/init?${q}`, "failed");
      if (!r?.data?.redirectUrl) throw new SingpassError("failed");
      return { redirectUrl: r.data.redirectUrl };
    },
    async getSession(token) {
      const r = await get<{ data: MyinfoPerson }>(
        `/api/singpass/session/${encodeURIComponent(token)}`,
        "session_lost",
      );
      return r.data;
    },
  };
}

export interface VerifyFields {
  kycEmail: string;
  mobile: string;
  /** A Myinfo marital code ("1", "2", …) or "" (unselected). */
  maritalStatus: string;
}

export interface KycApi {
  /** SG `POST /usersPrivate/authenticate` with the SingPass token (identity fields come from the server). */
  authenticate(input: { uid: string; token: string; fields: VerifyFields }): Promise<void>;
}

export function createKycApi(api: Api): KycApi {
  return {
    async authenticate({ uid, token, fields }) {
      if (api.regionClient.region !== "sg") throw new Error("SingPass verification is SG only");
      await unwrap(
        api.regionClient.client.POST("/api/sg/usersPrivate/authenticate", {
          body: {
            userPrivate: {
              _id: uid,
              kycEmail: fields.kycEmail.trim(),
              mobile: fields.mobile.trim(),
              maritalStatus: fields.maritalStatus,
            },
            singpassSessionToken: token,
          },
        }),
      );
    },
  };
}

export const kycQueryKeys = {
  all: ["kyc"] as const,
  singpassSession: (token: string) => ["kyc", "singpass-session", token] as const,
  /** HK (W-8): the caller's latest ShuftiPro verification. */
  shuftiLatest: () => ["kyc", "shufti-latest"] as const,
};

/**
 * Myinfo marital status codes (`backend/singpass` data_mapper), stored as the
 * code — as mobile does. `4` is not issued.
 */
export const MARITAL_STATUS_CODES = [
  { code: "1", labelKey: "verify.marital.1" },
  { code: "2", labelKey: "verify.marital.2" },
  { code: "3", labelKey: "verify.marital.3" },
  { code: "5", labelKey: "verify.marital.5" },
  { code: "6", labelKey: "verify.marital.6" },
] as const;

/** A Myinfo marital value → a known code, else "" (unselected). */
export function maritalPrefill(raw: string | null | undefined): string {
  const v = (raw ?? "").trim();
  return MARITAL_STATUS_CODES.some((m) => m.code === v) ? v : "";
}

const MASK = "••••";

/**
 * First character + "••••" + last 4 (`S1234567A` → `S••••567A`), the same rule
 * as the server's profile view. Spaces and punctuation are dropped first.
 */
export function maskNric(raw: string | null | undefined): string | undefined {
  const id = (raw ?? "").replace(/[^A-Za-z0-9]/g, "");
  if (!id) return undefined;
  if (id.length <= 5) return `${id.charAt(0)}${MASK}`;
  return `${id.charAt(0)}${MASK}${id.slice(-4)}`;
}

/** The editable fields prefilled from Myinfo. */
export function verifyPrefill(person: MyinfoPerson): VerifyFields {
  return {
    kycEmail: person.email ?? "",
    mobile: person.mobile ?? "",
    maritalStatus: maritalPrefill(person.maritalStatus),
  };
}

export type VerifyField = keyof VerifyFields;

/** Field → i18n error key; empty when valid. */
export function validateVerifyFields(f: VerifyFields): Partial<Record<VerifyField, string>> {
  const errors: Partial<Record<VerifyField, string>> = {};
  if (!f.kycEmail.trim()) errors.kycEmail = "verify.validation.emailRequired";
  else if (!isValidEmail(f.kycEmail.trim())) errors.kycEmail = "verify.validation.emailInvalid";
  if (!f.mobile.trim()) errors.mobile = "verify.validation.mobileRequired";
  if (maritalPrefill(f.maritalStatus) === "") errors.maritalStatus = "verify.validation.maritalRequired";
  return errors;
}

/** Any verify-flow failure → the RecoveryView action ("Try again" restarts SingPass, plus support). */
export function singpassRecovery(failureOrError: SingpassFailure | unknown): RecoveryAction {
  let failure: SingpassFailure;
  let correlationId: string | undefined;
  if (typeof failureOrError === "string" && (SINGPASS_FAILURES as readonly string[]).includes(failureOrError)) {
    failure = failureOrError as SingpassFailure;
  } else if (failureOrError instanceof SingpassError) {
    failure = failureOrError.failure;
  } else if (failureOrError instanceof ApiError) {
    // authenticate 400/404: the token is spent before the write, so restart.
    const s = failureOrError.status;
    failure = s === 429 ? "rate_limited" : s === 400 || s === 404 ? "session_lost" : "failed";
    correlationId = failureOrError.correlationId;
  } else {
    failure = "failed";
  }
  return {
    errorCode: `singpass_${failure}`,
    action: "retry",
    kind: "recoverable",
    messageKey: `errors.singpass_${failure}`,
    ...(correlationId ? { correlationId } : {}),
  };
}

/**
 * A failed `authenticate`. `resubmit`: no usable answer (network, timeout,
 * 5xx), so the token may still be `consumed` — keep it and let the customer
 * send again. Otherwise `action` is the RecoveryView: 400/404 (token spent)
 * and 429 restart SingPass; any other status keeps the server's action.
 */
export type AuthenticateFailure =
  | { resubmit: true; messageKey: string; correlationId?: string }
  | { resubmit: false; action: RecoveryAction };

export function authenticateFailure(err: unknown): AuthenticateFailure {
  if (err instanceof NetworkError || (err instanceof ApiError && err.status >= 500)) {
    const correlationId = err instanceof ApiError ? err.correlationId : undefined;
    return { resubmit: true, messageKey: "verify.submitFailed", ...(correlationId ? { correlationId } : {}) };
  }
  if (err instanceof ApiError && ![400, 404, 429].includes(err.status)) {
    return { resubmit: false, action: toRecoveryAction(err) };
  }
  return { resubmit: false, action: singpassRecovery(err) };
}

// ================================================================ HK: ShuftiPro (W-8)
//
// Server-created verification by full-page redirect and return:
//   1. `createVerification(fullAddress)` → the backend creates the ShuftiPro
//      request (camera-only capture) and returns its hosted `verificationUrl`.
//      The screen stores `returnTo` and `kn.verify.startedAt` (reference and
//      time only) and leaves the page.
//   2. ShuftiPro redirects back to `/account/verify?shufti=returned`.
//   3. `latest()` is the ONLY source of the result — the backend's record,
//      set by ShuftiPro's signed callback. The client never posts a result.

/** sessionStorage: the verification this tab started (`{reference, startedAt}`), never identity data. */
export const VERIFY_STARTED_KEY = "kn.verify.startedAt";
/** The query param ShuftiPro's `redirect_url` carries back (`?shufti=returned`). */
export const SHUFTI_RETURN_PARAM = "shufti";
export const SHUFTI_RETURN_VALUE = "returned";
/** Poll `latest` every 3 s for up to 2 min on return (the HK exception to W-7's no-poll rule). */
export const SHUFTI_POLL_INTERVAL_MS = 3_000;
export const SHUFTI_POLL_WINDOW_MS = 120_000;
export const SHUFTI_ADDRESS_MAX = 300;

export type ShuftiStatus = "pending" | "accepted" | "declined" | "cancelled" | "timeout" | "invalid";

export interface ShuftiVerification {
  reference: string;
  status: ShuftiStatus;
  declinedReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ShuftiStart {
  reference: string;
  verificationUrl: string;
  createdAt: string;
}

export interface ShuftiApi {
  /** HK `POST /usersPrivate/shuftipro/verification`. 409 = already verified, 502 = provider down. */
  createVerification(fullAddress: string): Promise<ShuftiStart>;
  /** HK `GET /usersPrivate/shuftipro/verification/latest`; `null` on 404 (none yet). */
  latest(): Promise<ShuftiVerification | null>;
}

export function createShuftiApi(api: Api): ShuftiApi {
  const hk = () => {
    if (api.regionClient.region !== "hk") throw new Error("ShuftiPro verification is HK only");
    return api.regionClient.client;
  };
  return {
    async createVerification(fullAddress) {
      const r = await unwrap(
        hk().POST("/api/hk/usersPrivate/shuftipro/verification", {
          body: { fullAddress: fullAddress.trim() },
        }),
      );
      return r.data;
    },
    async latest() {
      try {
        const r = await unwrap(hk().GET("/api/hk/usersPrivate/shuftipro/verification/latest", {}));
        return r?.data ?? null;
      } catch (err) {
        if (err instanceof ApiError && err.status === 404) return null;
        throw err;
      }
    },
  };
}

/** What this tab started (stored in sessionStorage as JSON). */
export interface ShuftiStarted {
  reference: string;
  startedAt: string;
}

export function serializeStarted(s: ShuftiStarted): string {
  return JSON.stringify({ reference: s.reference, startedAt: s.startedAt });
}

/** Tolerant read: anything malformed is `null` (→ Start). */
export function parseStarted(raw: string | null | undefined): ShuftiStarted | null {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<ShuftiStarted>;
    if (typeof v?.reference === "string" && v.reference && typeof v.startedAt === "string") {
      return { reference: v.reference, startedAt: v.startedAt };
    }
  } catch {
    // fall through
  }
  return null;
}

/** Address field → i18n error key; undefined when valid. */
export function validateHkAddress(raw: string): string | undefined {
  const v = raw.trim();
  if (!v) return "verify.hk.validation.addressRequired";
  if (v.length > SHUFTI_ADDRESS_MAX) return "verify.hk.validation.addressTooLong";
  return undefined;
}

/** Every failure the HK verify screen can show (`errors.shufti_<code>`). */
export type ShuftiFailure = "declined" | "cancelled" | "timeout" | "invalid" | "unavailable";

/**
 * A terminal non-accepted status, or a create failure → the RecoveryView
 * action. Always "Try again" (restart at Start); never "Failed to verify".
 * A server error that is not the provider being down (e.g. a 500 config
 * error) keeps the server's own action.
 */
export function shuftiRecovery(statusOrError: ShuftiFailure | Exclude<ShuftiStatus, "pending" | "accepted"> | unknown): RecoveryAction {
  let failure: ShuftiFailure;
  let correlationId: string | undefined;
  if (
    typeof statusOrError === "string" &&
    ["declined", "cancelled", "timeout", "invalid", "unavailable"].includes(statusOrError)
  ) {
    failure = statusOrError as ShuftiFailure;
  } else if (statusOrError instanceof NetworkError) {
    failure = "unavailable";
  } else if (statusOrError instanceof ApiError) {
    if (statusOrError.status !== 502 && statusOrError.status !== 503 && statusOrError.status !== 504) {
      return toRecoveryAction(statusOrError);
    }
    failure = "unavailable";
    correlationId = statusOrError.correlationId;
  } else {
    failure = "unavailable";
  }
  return {
    errorCode: `shufti_${failure}`,
    action: "retry",
    kind: "recoverable",
    messageKey: `errors.shufti_${failure}`,
    ...(correlationId ? { correlationId } : {}),
  };
}
